import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { getGuestPaymentStatus, getGuestPaymentStatusByAccessToken, getGuestPlayback, getMovie, getPaymentStatus, getPlayback } from '../services/api';
import { Movie, PlaybackResponse } from '../types/api';

declare global {
  interface Window {
    Stream?: (iframe: HTMLIFrameElement) => {
      play: () => Promise<void>;
      muted: boolean;
    };
  }
}

const STREAM_SDK_URL = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';

export const PlaybackPage: React.FC = () => {
  const { id = '', accessToken = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { t, formatDateTime } = useI18n();
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null);
  const [movie, setMovie] = useState<Movie | null>(null);
  const [error, setError] = useState('');
  const isGuest = Boolean(accessToken);
  const queryPaymentId = Number(searchParams.get('paymentId') || 0);
  const [guestPaymentId, setGuestPaymentId] = useState(queryPaymentId);
  const [guestMovieId, setGuestMovieId] = useState(searchParams.get('movieId') || '');
  const paymentId = isGuest ? guestPaymentId : queryPaymentId;
  const movieId = isGuest ? guestMovieId : id;
  const [paymentConfirmed, setPaymentConfirmed] = useState(!isGuest && !queryPaymentId);
  const [checkingPayment, setCheckingPayment] = useState(isGuest || Boolean(queryPaymentId));
  const [startingPlayback, setStartingPlayback] = useState(false);

  useEffect(() => {
    if (!movieId) return;
    let active = true;
    getMovie(movieId)
      .then((response) => {
        if (active) setMovie(response.data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [movieId]);

  useEffect(() => {
    if (!isGuest && !paymentId) {
      setPaymentConfirmed(true);
      setCheckingPayment(false);
      return;
    }

    let active = true;
    let timer: number | undefined;
    const checkPayment = async () => {
      try {
        const response = isGuest
          ? paymentId
            ? await getGuestPaymentStatus(paymentId, accessToken)
            : await getGuestPaymentStatusByAccessToken(accessToken)
          : await getPaymentStatus(paymentId);
        if (!active) return;
        if (isGuest && !paymentId) {
          setGuestPaymentId(response.payment.id);
        }
        if (isGuest && !guestMovieId) {
          setGuestMovieId(String(response.payment.movieId));
        }
        if (response.payment.status === 'succeeded' && response.rental) {
          setPaymentConfirmed(true);
          setCheckingPayment(false);
          setError('');
          if (timer) window.clearInterval(timer);
          return;
        }
        if (response.payment.status === 'failed' || response.payment.status === 'cancelled') {
          setCheckingPayment(false);
          setError(t('paymentFailed'));
          if (timer) window.clearInterval(timer);
        }
      } catch {
        if (active) {
          setCheckingPayment(false);
          setError(t('paymentStatusError'));
        }
      }
    };

    setCheckingPayment(true);
    checkPayment();
    timer = window.setInterval(checkPayment, 2000);
    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
    };
  }, [accessToken, guestMovieId, isGuest, paymentId, t]);

  useEffect(() => {
    if (!playback?.playbackUrl) return;
    const timer = window.setInterval(async () => {
      try {
        const response = isGuest ? await getGuestPlayback(accessToken) : await getPlayback(id);
        setPlayback(response);
      } catch {
        setError(t('playbackUnavailable'));
      }
    }, 150000);

    return () => window.clearInterval(timer);
  }, [accessToken, id, isGuest, playback?.playbackUrl, t]);

  useEffect(() => {
    if (!playback?.playbackUrl) return;

    let disposed = false;
    let scriptElement: HTMLScriptElement | null = null;

    const tryAutoPlay = () => {
      const iframe = document.getElementById('stream-player');
      if (disposed || !(iframe instanceof HTMLIFrameElement) || typeof window.Stream !== 'function') return;

      const player = window.Stream(iframe);
      player.play().catch(() => {
        if (disposed) return;
        player.muted = true;
        player.play().catch(() => undefined);
      });
    };

    const existingScript = document.querySelector(`script[src="${STREAM_SDK_URL}"]`) as HTMLScriptElement | null;
    if (window.Stream) {
      tryAutoPlay();
    } else if (existingScript) {
      scriptElement = existingScript;
      scriptElement.addEventListener('load', tryAutoPlay);
    } else {
      scriptElement = document.createElement('script');
      scriptElement.src = STREAM_SDK_URL;
      scriptElement.async = true;
      scriptElement.addEventListener('load', tryAutoPlay);
      document.body.appendChild(scriptElement);
    }

    return () => {
      disposed = true;
      scriptElement?.removeEventListener('load', tryAutoPlay);
    };
  }, [playback?.playbackUrl]);

  const startPlayback = async () => {
    if (!paymentConfirmed) return;
    setStartingPlayback(true);
    setError('');
    try {
      const response = isGuest ? await getGuestPlayback(accessToken) : await getPlayback(id);
      setPlayback(response);
    } catch {
      setError(t('playbackUnavailable'));
    } finally {
      setStartingPlayback(false);
    }
  };

  const renderWaiting = () => (
    <div className="grid aspect-video w-full place-items-center bg-black p-6 text-center text-white">
      <div>
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-stone-700 border-t-[#ffd460]" />
        <p className="mt-4 font-semibold">{t('confirmingPayment')}</p>
        <p className="mt-2 text-sm text-stone-400">{t('webhookDelay')}</p>
      </div>
    </div>
  );

  const renderStartPanel = () => (
    <aside className="h-fit rounded-lg border border-stone-800 bg-stone-800 p-6 shadow-sm">
      <div className="text-sm font-medium text-stone-300">{t('firstPlaybackWindow')}</div>
      <p className="mt-3 text-sm leading-6 text-stone-300">{t('startMovieNotice')}</p>
      <button
        className="mt-6 w-full rounded-md bg-[#ffd460] px-4 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d] disabled:cursor-not-allowed disabled:opacity-60"
        onClick={startPlayback}
        disabled={startingPlayback}
        type="button"
      >
        {startingPlayback ? t('preparingPlayback') : t('startMovie')}
      </button>
    </aside>
  );

  const renderMovieDetails = () => {
    if (!movie || playback) return null;
    const actors = movie.actors?.trim();
    return (
      <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
        <section>
          <div className="overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
            <img src={movie.imageUrl} alt={movie.title} className="aspect-video w-full object-cover" />
          </div>
          <h1 className="mt-6 text-4xl font-bold text-white">{movie.title}</h1>
          {actors && (
            <p className="mt-3 text-base leading-7 text-stone-300">
              <span className="font-semibold text-stone-100">{t('cast')}:</span> {actors}
            </p>
          )}
          <p className="mt-4 text-lg leading-8 text-stone-300">{movie.description}</p>
        </section>
        {renderStartPanel()}
      </div>
    );
  };

  const renderPlayer = () => (
    <iframe
      id="stream-player"
      title={t('playerTitle')}
      src={playback!.playbackUrl}
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
      loading="lazy"
      className="aspect-video w-full"
      height="720"
      width="1280"
    />
  );

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{playback?.movieTitle || movie?.title || t('playback')}</h1>
          {playback && <p className="text-sm text-stone-300">{t('rentalExpires', { date: formatDateTime(playback.rentalExpiresAt) })}</p>}
        </div>
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      ) : !checkingPayment && !playback && movie ? (
        renderMovieDetails()
      ) : (
        <div className="overflow-hidden rounded-lg bg-black">
          {checkingPayment ? renderWaiting() : playback ? renderPlayer() : renderStartPanel()}
        </div>
      )}
    </main>
  );
};
